import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Clock, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import LightBackground from '@/components/LightBackground';
import { useAdminSettings } from '@/hooks/useAdminSettings';

const RefundPolicy = () => {
  const lastUpdated = 'February 3, 2026';
  const { monthlyPrice, lifetimePrice } = useAdminSettings();

  return (
    <>
      <Helmet>
        <title>Refund Policy – Faceless Aura Club</title>
        <meta 
          name="description" 
          content="Refund Policy for Faceless Aura Club. Learn about our 7-day money-back guarantee and cancellation process." 
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative">
        <LightBackground />
        
        {/* Header */}
        <header className="relative z-10 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="relative z-10 max-w-4xl mx-auto px-6 py-12 md:py-16">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 mb-6">
              <RotateCcw className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Refund Policy
            </h1>
            <p className="text-slate-600">
              Last updated: {lastUpdated}
            </p>
          </div>

          {/* Guarantee Banner */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 md:p-8 mb-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              7-Day Money-Back Guarantee
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              We stand behind our content. If you're not satisfied with your purchase, 
              request a full refund within 7 days—no questions asked.
            </p>
          </div>

          {/* Policy Content */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-10 space-y-10">
            
            {/* Overview */}
            <section>
              <p className="text-slate-700 leading-relaxed">
                At Faceless Aura Club, we want you to be completely satisfied with your investment in your 
                education. This Refund Policy outlines how refunds and cancellations work for our different 
                membership plans.
              </p>
            </section>

            {/* Lifetime Access */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-semibold text-slate-900">Lifetime Access (${lifetimePrice})</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-medium text-green-800">✓ 7-Day Money-Back Guarantee</p>
                </div>
                <p><strong>Eligibility:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Request must be made within 7 days of purchase</li>
                  <li>Available for first-time purchases only</li>
                  <li>No questions asked—we respect your decision</li>
                </ul>
                <p><strong>How to Request:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Email <a href="mailto:support@facelessaura.club" className="text-blue-600 hover:text-blue-700 underline">support@facelessaura.club</a> with your purchase email</li>
                  <li>Include "Refund Request" in the subject line</li>
                  <li>We'll process your refund within 5-10 business days</li>
                </ul>
                <p><strong>After 7 Days:</strong> Lifetime access purchases are non-refundable after the 7-day guarantee period. This is because you retain permanent access to all content.</p>
              </div>
            </section>

            {/* Monthly Subscription */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-slate-900">Monthly Subscription (${monthlyPrice}/month)</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p><strong>Cancellation:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Cancel anytime from your account dashboard</li>
                  <li>Or email <a href="mailto:support@facelessaura.club" className="text-blue-600 hover:text-blue-700 underline">support@facelessaura.club</a></li>
                  <li>Access continues until the end of your current billing period</li>
                  <li>No further charges after cancellation</li>
                </ul>
                <p><strong>Refunds:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>We do not offer partial refunds for unused portions of a billing period</li>
                  <li>If you experience technical issues preventing access, contact support for resolution or credit</li>
                </ul>
              </div>
            </section>

            {/* Free Trial */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <HelpCircle className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-slate-900">Free Trial (Freemium)</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>Our free trial provides limited access to introductory content at no cost.</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>No payment information required to start</li>
                  <li>No commitment or automatic charges</li>
                  <li>Upgrade anytime to unlock full access</li>
                </ul>
              </div>
            </section>

            {/* Non-Refundable */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <XCircle className="w-6 h-6 text-red-500" />
                <h2 className="text-xl font-semibold text-slate-900">Non-Refundable Situations</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>Refunds will NOT be issued in the following cases:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Requests made after the 7-day guarantee period (lifetime access)</li>
                  <li>Account termination due to Terms of Service violations</li>
                  <li>Chargebacks or payment disputes (these may result in account suspension)</li>
                  <li>Repeat refund requests from the same individual</li>
                  <li>Dissatisfaction unrelated to the quality of content (e.g., change of mind after 7 days)</li>
                </ul>
              </div>
            </section>

            {/* Refund Process */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">How Refunds Are Processed</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-2">1</div>
                    <p className="font-medium">Submit Request</p>
                    <p className="text-sm text-slate-500">Email us with your details</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-2">2</div>
                    <p className="font-medium">Review</p>
                    <p className="text-sm text-slate-500">We verify eligibility (1-2 days)</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-2">3</div>
                    <p className="font-medium">Refund Issued</p>
                    <p className="text-sm text-slate-500">Funds returned in 5-10 days</p>
                  </div>
                </div>
                <p className="text-sm text-slate-500">
                  Refunds are processed to the original payment method. Depending on your bank, 
                  it may take additional time for the funds to appear in your account.
                </p>
              </div>
            </section>

            {/* Disputes */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Payment Disputes</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  If you have a billing issue, please contact us at{' '}
                  <a href="mailto:support@facelessaura.club" className="text-blue-600 hover:text-blue-700 underline">
                    support@facelessaura.club
                  </a>{' '}
                  before initiating a chargeback with your bank.
                </p>
                <p>
                  We're committed to resolving issues fairly. Filing a chargeback without first contacting 
                  us may result in account suspension and loss of access to purchased content.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Questions?</h2>
              <div className="text-slate-700 leading-relaxed space-y-2">
                <p>If you have questions about refunds or cancellations, we're here to help:</p>
                <p>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:support@facelessaura.club" className="text-blue-600 hover:text-blue-700 underline">
                    support@facelessaura.club
                  </a>
                </p>
                <p>We typically respond within 24-48 hours.</p>
              </div>
            </section>

          </div>

          {/* Footer links */}
          <div className="text-center mt-8 space-x-4">
            <Link 
              to="/terms" 
              className="text-blue-600 hover:text-blue-700 underline underline-offset-4"
            >
              Terms of Service
            </Link>
            <span className="text-slate-300">|</span>
            <Link 
              to="/privacy" 
              className="text-blue-600 hover:text-blue-700 underline underline-offset-4"
            >
              Privacy Policy
            </Link>
            <span className="text-slate-300">|</span>
            <Link 
              to="/" 
              className="text-blue-600 hover:text-blue-700 underline underline-offset-4"
            >
              Return to Homepage
            </Link>
          </div>
        </main>
      </div>
    </>
  );
};

export default RefundPolicy;
